import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  TouchableOpacity,
  TextInput,
  Image,
  StyleSheet,
  useWindowDimensions,
  ScrollView,
} from 'react-native';
import Text from '../../components/text';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import ajax from '../../helpers/ajaxHelper';
import Urls from '../../services/Urls';
import debounce from 'lodash.debounce';
import drawerHoc from '../../components/drawerHOC/DrawerHOC';
import { COLORS, BORDER_RADIUS, SHADOWS, SPACING } from '../../constants/theme';
import { Ionicons, Feather } from '@expo/vector-icons';
import { navigate } from '../../navigation/RootNavigation';

// Sample default image for cards
const DEFAULT_PROPERTY_IMAGE = require('../../assets/images/property_sample_1.png');

const PropertyCard = ({ item, navigation }) => {
  const percentage = item?.percentage ? Math.round(item?.percentage) : 10;
  const name = item?.name || 'PROPERTY NAME';
  const postcode = item?.postcode || item?.city || 'Location';

  return (
    <View style={styles.card}>
      {/* Property Image Banner */}
      <View style={styles.cardImageContainer}>
        <Image
          source={DEFAULT_PROPERTY_IMAGE}
          style={styles.cardImage}
          resizeMode="cover"
        />
      </View>

      {/* Card Content */}
      <View style={styles.cardBody}>
        <Text style={styles.cardTitle} numberOfLines={1}>
          {name}
        </Text>
        <Text style={styles.cardPostcode} numberOfLines={1}>
          {postcode}
        </Text>

        {/* Progress Bar */}
        <View style={styles.progressContainer}>
          <Text style={styles.progressText}>{percentage}% complete</Text>
          <View style={styles.progressBarTrack}>
            <View style={[styles.progressBarFill, { width: `${percentage}%` }]} />
          </View>
        </View>

        {/* Buttons Row */}
        <View style={styles.cardActions}>
          <TouchableOpacity
            style={styles.viewDetailsBtn}
            onPress={() => navigate('PropertyInfo', { id: item?.id })}
            activeOpacity={0.8}
          >
            <Text style={styles.viewDetailsBtnText}>View Details</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.continueSetupBtn}
            onPress={() => navigate('PropertySection', { id: item?.id })}
            activeOpacity={0.8}
          >
            <Text style={styles.continueSetupBtnText}>Continue Setup</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const Properties = () => {
  const navigation = useNavigation();
  const [properties, setProperties] = useState([]);
  const [searchText, setSearchText] = useState('');
  const focused = useIsFocused();
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  useEffect(() => {
    ajax
      .get(Urls.Properties)
      .then(({ data }) => {
        if (data.status && data?.payload) {
          setProperties(data.payload);
        }
      })
      .catch((error) => {
        console.log('fetch properties error', error);
      });
  }, [focused]);

  const _onSearch = (text) => {
    ajax
      .post(Urls.SearchProperty, { searchText: text })
      .then(({ data }) => {
        if (data.status && data?.data) {
          setProperties(data.data);
        }
      })
      .catch((error) => {
        console.log('search property error', error);
      });
  };

  const _onSearchDebounce = useCallback(debounce(_onSearch, 600), []);

  const handleSearchChange = (text) => {
    setSearchText(text);
    if (text.trim()) {
      _onSearchDebounce(text);
    } else {
      ajax.get(Urls.Properties).then(({ data }) => {
        if (data.status && data?.payload) setProperties(data.payload);
      });
    }
  };

  return (
    <View style={styles.container}>
      {/* Page Header */}
      <View style={[styles.headerRow, isMobile && styles.headerRowMobile]}>
        <View style={styles.headerLeft}>
          <Text style={styles.pageTitle}>My Properties</Text>
          <Text style={styles.pageSubtitle}>Manage and track your portfolio</Text>
        </View>

        <View style={styles.headerRight}>
          <View style={styles.countBadge}>
            <Text style={styles.countBadgeText}>{properties.length} Properties</Text>
          </View>
          <TouchableOpacity
            style={styles.addPropertyBtn}
            onPress={() => navigation.push('PropertyAddress')}
            activeOpacity={0.85}
          >
            <Ionicons name="add" size={18} color="#FFFFFF" style={{ marginRight: 4 }} />
            <Text style={styles.addPropertyBtnText}>Add New Property</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Search and Filter Row */}
      <View style={styles.filterRow}>
        <View style={styles.searchBox}>
          <Ionicons name="search-outline" size={18} color={COLORS.textMuted} style={styles.searchIcon} />
          <TextInput
            style={styles.searchInput}
            placeholder="Search properties..."
            placeholderTextColor={COLORS.textMuted}
            value={searchText}
            onChangeText={handleSearchChange}
          />
        </View>

        <TouchableOpacity style={styles.filterBtn} activeOpacity={0.8}>
          <Feather name="sliders" size={16} color={COLORS.textSecondary} style={{ marginRight: 6 }} />
          <Text style={styles.filterBtnText}>Filter</Text>
        </TouchableOpacity>
      </View>

      {/* Property Cards Grid */}
      {properties.length > 0 ? (
        <View style={styles.gridContainer}>
          {properties.map((item, index) => (
            <View
              key={item.id || index}
              style={[
                styles.gridItem,
                isMobile && styles.gridItemMobile,
              ]}
            >
              <PropertyCard item={item} navigation={navigation} />
            </View>
          ))}
        </View>
      ) : (
        <View style={styles.emptyContainer}>
          <Ionicons name="home-outline" size={48} color={COLORS.primaryLight} />
          <Text style={styles.emptyTitle}>No Properties Found</Text>
          <Text style={styles.emptySubtitle}>Click "Add New Property" to get started with your portfolio.</Text>
          <TouchableOpacity
            style={[styles.addPropertyBtn, { marginTop: 16 }]}
            onPress={() => navigation.push('PropertyAddress')}
            activeOpacity={0.85}
          >
            <Ionicons name="add" size={18} color="#FFFFFF" style={{ marginRight: 4 }} />
            <Text style={styles.addPropertyBtnText}>Add New Property</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: COLORS.appBg,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    flexWrap: 'wrap',
    gap: 12,
  },
  headerRowMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  headerLeft: {
    flex: 1,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: COLORS.textPrimary,
  },
  pageSubtitle: {
    fontSize: 13,
    color: COLORS.textSecondary,
    marginTop: 2,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  countBadge: {
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  countBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: COLORS.textSecondary,
  },
  addPropertyBtn: {
    backgroundColor: COLORS.primary,
    borderRadius: BORDER_RADIUS.lg,
    paddingHorizontal: 18,
    paddingVertical: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    ...SHADOWS.sm,
  },
  addPropertyBtnText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 24,
  },
  searchBox: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.surface,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: BORDER_RADIUS.lg,
    paddingHorizontal: 14,
    height: 44,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: COLORS.textPrimary,
  },
  filterBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.surface,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: BORDER_RADIUS.lg,
    paddingHorizontal: 16,
    height: 44,
  },
  filterBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: COLORS.textSecondary,
  },
  gridContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -8,
  },
  gridItem: {
    width: '33.333%',
    paddingHorizontal: 8,
    marginBottom: 20,
  },
  gridItemMobile: {
    width: '100%',
  },
  // Card styles
  card: {
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: COLORS.border,
    overflow: 'hidden',
    ...SHADOWS.sm,
  },
  cardImageContainer: {
    height: 140,
    width: '100%',
    backgroundColor: COLORS.borderLight,
  },
  cardImage: {
    width: '100%',
    height: '100%',
  },
  cardBody: {
    padding: 16,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: COLORS.textPrimary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  cardPostcode: {
    fontSize: 12,
    color: COLORS.textSecondary,
    marginTop: 2,
    marginBottom: 12,
  },
  progressContainer: {
    marginBottom: 16,
  },
  progressText: {
    fontSize: 11,
    fontWeight: '600',
    color: COLORS.primary,
    marginBottom: 6,
  },
  progressBarTrack: {
    height: 6,
    backgroundColor: COLORS.borderLight,
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: COLORS.primary,
    borderRadius: 3,
  },
  cardActions: {
    flexDirection: 'row',
    gap: 8,
  },
  viewDetailsBtn: {
    flex: 1,
    backgroundColor: COLORS.surface,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: BORDER_RADIUS.md,
    paddingVertical: 9,
    alignItems: 'center',
    justifyContent: 'center',
  },
  viewDetailsBtnText: {
    fontSize: 12,
    fontWeight: '600',
    color: COLORS.textPrimary,
  },
  continueSetupBtn: {
    flex: 1,
    backgroundColor: COLORS.primary,
    borderRadius: BORDER_RADIUS.md,
    paddingVertical: 9,
    alignItems: 'center',
    justifyContent: 'center',
  },
  continueSetupBtnText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: COLORS.border,
    marginTop: 10,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: COLORS.textPrimary,
    marginTop: 12,
  },
  emptySubtitle: {
    fontSize: 13,
    color: COLORS.textSecondary,
    marginTop: 4,
    textAlign: 'center',
    maxWidth: 300,
  },
});

export default drawerHoc({
  Component: Properties,
});
